import React from "react";
import { reportError } from "@/shared/lib/logtail";
// Метка в URL — защита от бесконечного цикла «crash → clear → reload → crash».
// Если флаг уже стоит, значит мы уже один раз восстанавливались, второй заход
// автоматического сброса не делаем.
const RECOVERY_FLAG = "vp-recovered";
/**
 * Рассинхрон бандлов после деплоя / битый кэш чанков.
 * В т.ч. webpack `__webpack_require__`: `o[e].call` когда модуля нет в runtime.
 */
function isChunkLoadError(error) {
    const name = error.name || "";
    const message = error.message || "";
    const stack = error.stack || "";
    return (name === "ChunkLoadError" ||
        /loading chunk \d+ failed/i.test(message) ||
        /loading css chunk \d+ failed/i.test(message) ||
        /failed to fetch dynamically imported module/i.test(message) ||
        // Safari: undefined is not an object (evaluating 'o[e].call')
        // Chrome: Cannot read properties of undefined (reading 'call')
        /\[e\]\.call/i.test(message) ||
        /modules\[.*\]\.call/i.test(message) ||
        /reading ['"]call['"]/i.test(message) ||
        (/__webpack_require__/i.test(stack) &&
            /reading ['"]call['"]|\[e\]\.call/i.test(`${message}\n${stack}`)));
}
/**
 * React vs внешняя мутация DOM (переводчик браузера, расширения, инжекты):
 * insertBefore / removeChild NotFoundError. Storage тут ни при чём.
 */
function isDomMutationConflict(error) {
    const name = error.name || "";
    const message = error.message || "";
    if (name !== "NotFoundError" && name !== "DOMException")
        return false;
    return (/removeChild/i.test(message) ||
        /insertBefore/i.test(message) ||
        /The node (?:to be removed|before which)/i.test(message));
}
/** Краши, где clear storage вреден и бесполезен — только soft reload. */
function isSoftRecoveryError(error) {
    return isChunkLoadError(error) || isDomMutationConflict(error);
}
/**
 * Верхний ErrorBoundary всего приложения.
 *
 * Без него любой неперехваченный выброс во время рендера сносит всё дерево
 * React — пользователь видит пустой экран. Это та самая ситуация
 * «у некоторых пользователей приложение не загружается».
 *
 * Поведение при ошибке:
 *   1. Логируем + отправляем в BetterStack (см. shared/lib/logtail).
 *   2. ChunkLoadError / DOM NotFoundError — мягкий reload без очистки
 *      storage (иначе теряем attribution/utm; clear DOM-конфликт не лечит).
 *   3. Остальные краши — чистим localStorage/sessionStorage и reload
 *      с меткой ?vp-recovered=1.
 *
 * Никаких диалогов «что делать» пользователю не показываем — просто молча
 * восстанавливаемся. Если после восстановления ошибка повторилась (флаг в
 * URL уже стоит), цикл не запускаем повторно — рендерим null, пользователь
 * перезагрузит вручную.
 */
export class AppErrorBoundary extends React.Component {
    constructor() {
        super(...arguments);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        // eslint-disable-next-line no-console
        console.error("[AppErrorBoundary] App crashed:", error, errorInfo);
        reportError(error, {
            source: "AppErrorBoundary",
            componentStack: errorInfo.componentStack,
            chunkLoad: isChunkLoadError(error),
            domMutation: isDomMutationConflict(error),
        });
        if (typeof window === "undefined")
            return;
        // Защита от цикла: если мы уже восстанавливались — не уходим в новый
        // круг. Пусть пользователь обновит вручную.
        let url;
        try {
            url = new URL(window.location.href);
        }
        catch {
            return;
        }
        if (url.searchParams.has(RECOVERY_FLAG))
            return;
        // Soft-recovery: storage не трогаем.
        if (!isSoftRecoveryError(error)) {
            try {
                localStorage?.clear();
                sessionStorage?.clear();
            }
            catch {
                /* private mode / null storage / quota */
            }
        }
        url.searchParams.set(RECOVERY_FLAG, "1");
        // replace, чтобы кнопка «назад» не возвращала на крашнувший URL.
        window.location.replace(url.toString());
    }
    render() {
        if (!this.state.hasError)
            return this.props.children;
        // Во время авто-восстановления ничего не показываем — страница вот-вот
        // перезагрузится. Если мы попали сюда из guard'а (повторная ошибка) —
        // тоже null: голый экран лучше, чем зацикленный сброс.
        return null;
    }
}
export default AppErrorBoundary;
